/**
 * Material-UI theme configuration
 * WHO-aligned palette, shared surfaces, and component defaults for consistent admin UI
 */
import {
    createTheme,
    Theme,
    ThemeOptions,
    PaletteMode,
    alpha,
} from '@mui/material/styles';

/** Toolbar / listing row: text fields, selects, and primary buttons share this height */
const DENSE_CONTROL_HEIGHT_PX = 40;

/** Primary brand: indigo app chrome (matches main navigation) */
const brandPrimaryLight = {
    main: '#1a237e',
    light: '#534bae',
    /** Dark navy for headings on light surfaces and primary hover */
    dark: '#001c53',
    contrastText: '#ffffff',
} as const;

const brandPrimaryDark = {
    main: '#7986cb',
    light: '#aab6fe',
    dark: '#49599a',
    contrastText: '#0d1117',
} as const;

/**
 * Creates theme based on provided mode
 * @param mode - Theme mode (light or dark)
 * @returns Configured theme
 */
export const createAppTheme = (mode: PaletteMode): Theme => {
    const isLight = mode === 'light';

    const themeOptions: ThemeOptions = {
        palette: {
            mode,
            primary: isLight ? { ...brandPrimaryLight } : { ...brandPrimaryDark },
            secondary: {
                main: isLight ? '#dc004e' : '#f48fb1',
                light: isLight ? '#e33371' : '#f6a5c0',
                dark: isLight ? '#9a0036' : '#aa647b',
                contrastText: '#ffffff',
            },
            error: {
                main: '#d32f2f',
                light: '#ef5350',
                dark: '#c62828',
            },
            warning: {
                main: '#ed6c02',
            },
            info: {
                main: '#0288d1',
            },
            success: {
                main: '#2e7d32',
                light: '#4caf50',
                dark: '#1b5e20',
            },
            background: {
                default: isLight ? '#F9FAFB' : '#121212',
                paper: isLight ? '#ffffff' : '#1e1e1e',
            },
            text: {
                primary: isLight ? 'rgba(15, 23, 42, 0.92)' : 'rgba(255, 255, 255, 0.87)',
                secondary: isLight ? 'rgba(15, 23, 42, 0.58)' : 'rgba(255, 255, 255, 0.6)',
                disabled: isLight ? 'rgba(15, 23, 42, 0.38)' : 'rgba(255, 255, 255, 0.38)',
            },
            divider: isLight ? 'rgba(15, 23, 42, 0.09)' : 'rgba(255, 255, 255, 0.12)',
            action: {
                hover: isLight ? 'rgba(15, 23, 42, 0.06)' : 'rgba(255, 255, 255, 0.08)',
                selected: isLight ? 'rgba(26, 35, 126, 0.08)' : 'rgba(121, 134, 203, 0.16)',
                disabledBackground: isLight ? 'rgba(15, 23, 42, 0.12)' : 'rgba(255, 255, 255, 0.12)',
            },
        },
        typography: {
            fontFamily: [
                '-apple-system',
                'BlinkMacSystemFont',
                '"Segoe UI"',
                'Roboto',
                '"Helvetica Neue"',
                'Arial',
                'sans-serif',
            ].join(','),
            h1: { fontSize: '2.5rem', fontWeight: 600, letterSpacing: '-0.02em' },
            h2: { fontSize: '2rem', fontWeight: 600, letterSpacing: '-0.02em' },
            h3: { fontSize: '1.75rem', fontWeight: 600, letterSpacing: '-0.015em' },
            h4: { fontSize: '1.5rem', fontWeight: 600, letterSpacing: '-0.015em' },
            h5: { fontSize: '1.25rem', fontWeight: 600 },
            h6: { fontSize: '1rem', fontWeight: 600 },
            subtitle1: { fontWeight: 500 },
            button: { fontWeight: 600, letterSpacing: '0.02em' },
        },
        spacing: 8,
        shape: {
            borderRadius: 8,
        },
        components: {
            MuiCssBaseline: {
                styleOverrides: {
                    body: ({ theme }: { theme: Theme }) => ({
                        backgroundColor: theme.palette.background.default,
                    }),
                },
            },
            MuiAppBar: {
                defaultProps: {
                    elevation: 0,
                    color: 'primary',
                },
                styleOverrides: {
                    root: ({ theme }: { theme: Theme }) => ({
                        backgroundImage:
                            theme.palette.mode === 'light'
                                ? `linear-gradient(105deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 55%, ${theme.palette.primary.light} 160%)`
                                : undefined,
                        boxShadow: 'none',
                        borderBottom: `1px solid ${alpha(theme.palette.primary.contrastText, 0.12)}`,
                    }),
                },
            },
            MuiButton: {
                defaultProps: {
                    disableElevation: true,
                },
                styleOverrides: {
                    root: {
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: 0,
                    },
                    sizeMedium: {
                        minHeight: DENSE_CONTROL_HEIGHT_PX,
                        paddingBlock: '8px',
                        paddingInline: '16px',
                        fontSize: '0.875rem',
                    },
                    sizeSmall: {
                        minHeight: DENSE_CONTROL_HEIGHT_PX,
                        paddingBlock: '8px',
                        paddingInline: '12px',
                        fontSize: '0.875rem',
                    },
                    sizeLarge: {
                        minHeight: 48,
                        fontSize: '0.9375rem',
                    },
                },
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: ({ ownerState }) => ({
                        ...(ownerState.size === 'small' &&
                            !ownerState.multiline && {
                                minHeight: DENSE_CONTROL_HEIGHT_PX,
                            }),
                    }),
                },
            },
            MuiFormControl: {
                defaultProps: {
                    size: 'small',
                },
            },
            MuiSelect: {
                defaultProps: {
                    size: 'small',
                    variant: 'outlined',
                },
            },
            MuiAutocomplete: {
                defaultProps: {
                    size: 'small',
                },
            },
            MuiPaper: {
                styleOverrides: {
                    root: {
                        backgroundImage: 'none',
                    },
                    elevation1: ({ theme }: { theme: Theme }) =>
                        theme.palette.mode === 'light'
                            ? {
                                  boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
                              }
                            : {},
                },
            },
            MuiCard: {
                styleOverrides: {
                    root: ({ theme }: { theme: Theme }) => ({
                        boxShadow:
                            theme.palette.mode === 'light'
                                ? '0 1px 3px rgba(15, 23, 42, 0.08)'
                                : '0 2px 8px rgba(0,0,0,0.35)',
                        borderRadius: theme.shape.borderRadius,
                    }),
                },
            },
            MuiTextField: {
                defaultProps: {
                    variant: 'outlined',
                    size: 'small',
                },
            },
            MuiDialog: {
                styleOverrides: {
                    paper: ({ theme }: { theme: Theme }) => ({
                        borderRadius: theme.shape.borderRadius,
                    }),
                },
            },
            MuiDialogTitle: {
                styleOverrides: {
                    root: ({ theme }: { theme: Theme }) => ({
                        fontSize: '1.125rem',
                        fontWeight: 600,
                        lineHeight: 1.4,
                        letterSpacing: '-0.01em',
                        color: theme.palette.text.primary,
                        padding: theme.spacing(2, 3),
                        paddingBottom: theme.spacing(1.5),
                    }),
                },
            },
            MuiDialogContent: {
                styleOverrides: {
                    root: ({ theme }: { theme: Theme }) => ({
                        padding: theme.spacing(2, 3),
                        // Enough top space for OutlinedInput / Select shrunk labels (not clipped by overflow)
                        paddingTop: theme.spacing(2.5),
                        // MUI default: `.MuiDialogTitle-root + & { paddingTop: 0 }` — that clips the first field’s label
                        '.MuiDialogTitle-root + &': {
                            paddingTop: theme.spacing(2.5),
                        },
                    }),
                },
            },
            MuiDialogActions: {
                styleOverrides: {
                    root: ({ theme }: { theme: Theme }) => ({
                        padding: theme.spacing(1.5, 3, 2, 3),
                        gap: theme.spacing(1.25),
                    }),
                },
            },
            MuiSwitch: {
                styleOverrides: {
                    switchBase: ({ theme }: { theme: Theme }) => ({
                        '&.Mui-checked': {
                            color: theme.palette.primary.main,
                            '& + .MuiSwitch-track': {
                                backgroundColor: theme.palette.primary.main,
                            },
                        },
                    }),
                },
            },
            MuiTableCell: {
                styleOverrides: {
                    head: ({ theme }: { theme: Theme }) => ({
                        fontWeight: 600,
                        backgroundColor: theme.palette.mode === 'light' ? theme.palette.grey[50] : theme.palette.grey[900],
                        color: theme.palette.text.primary,
                    }),
                },
            },
        },
    };

    return createTheme(themeOptions);
};

export const theme = createAppTheme('light');
